import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Button } from "@mui/material";
import HomeRepairServiceOutlinedIcon from "@mui/icons-material/HomeRepairServiceOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { selectUser } from "../../../../features/userSlice";
import {
  CONTACT_METHOD_LABELS,
  addressLabel,
  customerLabel,
  daysActive,
  formatDate,
} from "../../../../components/WindowDoor/ServiceRequests/constants";
import {
  fetchServiceRequest,
} from "../../../../components/WindowDoor/ServiceRequests/api";
import {
  ActivityLogButton,
  AttachmentsCard,
  ChatThread,
  ProgressTimeline,
  DetailRow,
  StatusChip,
  SummaryCard,
} from "./ServiceRequestShared";
import { PageHeader, PageShell } from "../../../../components/UI/Page";
import { LoadingState } from "../../../../components/UI/States";
import { BackLink } from "../../../../components/WindowDoor/wdUi";

/**
 * The salesperson's view of their own request: read-only on everything the
 * shop owns, with the shared chat so they can chase it without a phone call.
 */
const MyServiceRequestDetail = () => {
  const { id } = useParams();
  const user = useSelector(selectUser);
  const navigate = useNavigate();

  const [request, setRequest] = useState(null);
  const [loadError, setLoadError] = useState(null);
  // Bumped after uploads so the chat rerenders the new system log line.
  const [chatVersion, setChatVersion] = useState(0);

  useEffect(() => {
    fetchServiceRequest(id)
      .then(setRequest)
      .catch((err) =>
        setLoadError(err.message || "Could not load this service request.")
      );
  }, [id]);

  if (loadError) {
    return (
      <PageShell size="sm">
        <BackLink onClick={() => navigate("/windowdoor/my-service-requests")}>
          My requests
        </BackLink>
        <Alert severity="error">{loadError}</Alert>
      </PageShell>
    );
  }

  if (!request) {
    return (
      <PageShell size="2xl">
        <div className="app-card rounded-2xl border">
          <LoadingState label="Loading service request…" />
        </div>
      </PageShell>
    );
  }

  const contactMethod = request.checklist?.contact_method
    ? CONTACT_METHOD_LABELS[request.checklist.contact_method]
    : null;

  return (
    <PageShell size="full">
      <BackLink onClick={() => navigate("/windowdoor/my-service-requests")}>
        My requests
      </BackLink>
      <PageHeader
        icon={HomeRepairServiceOutlinedIcon}
        tone="indigo"
        eyebrow="My Service Requests"
        title={`Service Request #${request.entry_number}`}
        description={
          <>
            Submitted {formatDate(request.entry_date)} · open{" "}
            {daysActive(request)} day{daysActive(request) === 1 ? "" : "s"}
          </>
        }
        actions={
          <>
            <ActivityLogButton requestId={request._id} />
            <Button
              variant="outlined"
              startIcon={<PictureAsPdfIcon />}
              href={`/api/windowdoor/servicerequests/${request._id}/pdf`}
              target="_blank"
              rel="noreferrer"
            >
              Download PDF
            </Button>
          </>
        }
      />

      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <SummaryCard label="Customer" value={customerLabel(request)} />
        <SummaryCard label="Contractor" value={request.contractor} />
        <SummaryCard
          label="Assigned To"
          value={request.assigned_to?.name || "Not yet assigned"}
          accent="border-l-indigo-500"
        />
        <SummaryCard label="Status" accent="border-l-amber-500">
          <StatusChip status={request.status} />
        </SummaryCard>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_26rem]">
        <div className="min-w-0 space-y-6">
          {/* Progress */}
          <div className="app-card rounded-2xl border">
            <div className="border-b app-divider px-5 py-3.5">
              <h2 className="font-semibold">Shop Progress</h2>
            </div>
            <div className="space-y-4 px-5 py-5">
              <ProgressTimeline request={request} />
              {contactMethod && (
                <p className="app-muted text-xs">
                  Homeowner contact: {contactMethod}
                </p>
              )}
              {request.checklist?.order_number && (
                <DetailRow
                  label="Order Number"
                  value={request.checklist.order_number}
                />
              )}
              <p className="app-muted border-t app-divider pt-3 text-xs">
                Last updated {formatDate(request.status_updated_at)}
                {request.status_updated_by?.name &&
                  ` by ${request.status_updated_by.name}`}
                .
              </p>
            </div>
          </div>

          {/* What was submitted */}
          <div className="app-card rounded-2xl border">
            <div className="border-b app-divider px-5 py-3.5">
              <h2 className="font-semibold">What You Submitted</h2>
            </div>
            <div className="grid gap-x-6 gap-y-4 px-5 py-5 sm:grid-cols-2 lg:grid-cols-3">
              <DetailRow label="Manufacturer" value={request.manufacturer} />
              <DetailRow label="Sales Number" value={request.sales_number} />
              <DetailRow
                label="Home Occupied"
                value={request.home_occupied ? "Yes" : "No"}
              />
              <DetailRow
                label="Address"
                value={addressLabel(request)}
                className="sm:col-span-2"
              />
              <DetailRow label="Zip" value={request.zip} />
              <DetailRow
                label="Preferred Contact"
                value={request.preferred_contact}
              />
              <DetailRow
                label="Customer Phone"
                value={request.customer_phone}
              />
              <DetailRow
                label="Customer Email"
                value={request.customer_email}
              />
              <DetailRow
                label="Age of Product"
                value={request.age_of_product}
              />
              <DetailRow label="CIG #" value={request.cig_number} />
              <DetailRow label="Product ID" value={request.product_id} />
              <DetailRow label="Bill To" value={request.bill_to} />
              <DetailRow label="Location" value={request.location} />
              <DetailRow label="Account #" value={request.account_number} />
              <DetailRow label="Job #" value={request.job_number} />
              <div className="sm:col-span-2 lg:col-span-3">
                <p className="app-muted text-xs font-medium">Describe Issue</p>
                <p className="app-surface mt-1.5 whitespace-pre-wrap rounded-xl border px-4 py-3 text-sm leading-relaxed">
                  {request.describe_issue || "—"}
                </p>
              </div>
            </div>
          </div>

          <AttachmentsCard
            request={request}
            user={user}
            onUploaded={() => {
              fetchServiceRequest(id).then(setRequest);
              setChatVersion((version) => version + 1);
            }}
          />
        </div>

        <ChatThread
          key={chatVersion}
          requestId={request._id}
          user={user}
          title={`Chat #${request.entry_number}`}
        />
      </div>
    </PageShell>
  );
};

export default MyServiceRequestDetail;
